import { useState, useRef, useEffect } from "react";
import {
  Upload, Download, Palette, Type, Sliders, CheckCircle2,
  Sparkles, Award, ShieldCheck, QrCode, Eye, RefreshCw,
  FileImage, Copy, Check, Printer, X, Image as ImageIcon,
  MoveVertical, MoveHorizontal, ZoomIn, ZoomOut
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

// Preset Luxury Templates
const PRESET_DESIGNS = [
  {
    id: "obsidian_gold",
    name: "Obsidian & 24K Gold",
    desc: "Luxury deep charcoal with dual gold foil borders and metallic accents",
    bgColor: "#0B0F19",
    borderColor: "#D4AF37",
    textColor: "#F8FAFC",
    accentColor: "#F59E0B",
    bgPattern: "gold-border"
  },
  {
    id: "academic_parchment",
    name: "Academic Parchment",
    desc: "Classic off-white university credential with engraved navy border",
    bgColor: "#FDFBF7",
    borderColor: "#1E3A8A",
    textColor: "#0F172A",
    accentColor: "#B45309",
    bgPattern: "classic-border"
  },
  {
    id: "royal_navy",
    name: "Royal Navy & Champagne",
    desc: "Deep indigo navy with champagne trim and heraldic seal styling",
    bgColor: "#0F172A",
    borderColor: "#E2E8F0",
    textColor: "#FFFFFF",
    accentColor: "#38BDF8",
    bgPattern: "navy-crest"
  },
  {
    id: "minimal_modern",
    name: "Modern Studio Minimalist",
    desc: "Sleek crisp white gallery aesthetic with bold modern typography",
    bgColor: "#FFFFFF",
    borderColor: "#0284C7",
    textColor: "#0F172A",
    accentColor: "#0284C7",
    bgPattern: "minimal-border"
  }
];

export default function AdvancedCertificateStudio({ open, onClose, onSaveTemplate }) {
  const [activeTab, setActiveTab] = useState("design"); // design | fields | typography | positioning | signatures
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const sigFileInputRef = useRef(null);

  // Template & Field Configuration State
  const [config, setConfig] = useState(() => {
    const saved = localStorage.getItem("ld_certificate_template_config");
    if (saved) {
      try { return JSON.parse(saved); } catch {}
    }
    return {
      templateId: "obsidian_gold",
      customBgImage: null, // Data URL or Image URL
      bgBrightness: 100,
      bgColor: "#0B0F19",
      borderColor: "#D4AF37",
      textColor: "#F8FAFC",
      accentColor: "#F59E0B",
      fontFamily: "Montserrat",
      recipientFontFamily: "Montserrat",
      
      // Dynamic Field Content
      orgName: "COLIBRI AI LABS & LAUNCHDECK ACADEMY",
      certTitle: "CERTIFICATE OF PROFESSIONAL MASTERY",
      subtitle: "ACCREDITED PROFESSIONAL CREDENTIAL",
      statementPrefix: "This certifies that",
      recipientName: "Sarah Jenkins",
      statementSuffix: "has satisfied all curriculum standards and autonomous agent engineering benchmarks for",
      courseTitle: "Learning Large Language Models: From Architecture to Autonomous Agents",
      certId: "CERT-2026-9842-X",
      issueDate: "October 31, 2026",
      expiryDate: "October 31, 2028",
      validityText: "Strict 2-Year Validity · Expires October 31, 2028",
      accreditationNotice: "Accredited under Global Artificial Intelligence & Autonomous Systems Standards",
      
      // Signatures & Badges
      signer1Name: "Dr. Andrew Lin",
      signer1Title: "Lead AI Research Scientist, Colibri AI Labs",
      signer1SigText: "Andrew Lin",
      signer1CustomSig: null,

      signer2Name: "Marcus Vance",
      signer2Title: "Dean of Professional AI Curriculums",
      signer2SigText: "Marcus Vance",
      signer2CustomSig: null,

      showQrCode: true,
      showGoldSeal: true,
      showSignatures: true,
      showExpiry: true,

      // Vertical (Y-Offset) Positioning Controls
      yOffsetOrg: 0,
      yOffsetTitle: 0,
      yOffsetRecipient: 0,
      yOffsetCourse: 0,
      yOffsetSignatures: 0,
      fontSizeRecipient: 28,
      fontSizeCourse: 22,
      recipientColor: "#F59E0B"
    };
  });

  const [isExporting, setIsExporting] = useState(false);

  // Synchronize canvas rendering whenever config changes
  useEffect(() => {
    renderCanvas();
  }, [config]);

  // Handle Custom Design Background Upload
  const handleBgUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG, JPG, SVG)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setConfig(prev => ({
        ...prev,
        templateId: "custom_uploaded",
        customBgImage: event.target.result
      }));
      toast.success("Custom certificate design loaded into canvas!");
    };
    reader.readAsDataURL(file);
  };

  // Handle Custom Signature Upload
  const handleSigUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setConfig(prev => ({
        ...prev,
        signer1CustomSig: event.target.result
      }));
      toast.success("Custom digital signature attached!");
    };
    reader.readAsDataURL(file);
  };

  // Select Preset Design
  const handleSelectPreset = (preset) => {
    setConfig(prev => ({
      ...prev,
      templateId: preset.id,
      customBgImage: null,
      bgColor: preset.bgColor,
      borderColor: preset.borderColor,
      textColor: preset.textColor,
      accentColor: preset.accentColor,
      recipientColor: preset.accentColor
    }));
    toast.success(`Applied ${preset.name} template`);
  };

  // High-Resolution Canvas Rendering Engine
  const renderCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = 1600;
    const height = 1000;
    canvas.width = width;
    canvas.height = height;

    const drawContent = () => {
      // 1. Draw Background
      if (config.customBgImage) {
        const bgImg = new Image();
        bgImg.crossOrigin = "anonymous";
        bgImg.onload = () => {
          ctx.drawImage(bgImg, 0, 0, width, height);
          drawOverlayElements(ctx, width, height);
        };
        bgImg.src = config.customBgImage;
        return;
      }

      // Default Preset Background
      ctx.fillStyle = config.bgColor;
      ctx.fillRect(0, 0, width, height);

      // Outer Decorative Border
      ctx.strokeStyle = config.borderColor;
      ctx.lineWidth = 14;
      ctx.strokeRect(40, 40, width - 80, height - 80);

      // Inner Trim Border
      ctx.strokeStyle = `${config.borderColor}80`;
      ctx.lineWidth = 2;
      ctx.strokeRect(55, 55, width - 110, height - 110);

      // Corner Accents
      const cornerSize = 40;
      ctx.fillStyle = config.borderColor;
      ctx.fillRect(35, 35, cornerSize, 6);
      ctx.fillRect(35, 35, 6, cornerSize);
      ctx.fillRect(width - 35 - cornerSize, 35, cornerSize, 6);
      ctx.fillRect(width - 41, 35, 6, cornerSize);
      ctx.fillRect(35, height - 41, cornerSize, 6);
      ctx.fillRect(35, height - 35 - cornerSize, 6, cornerSize);
      ctx.fillRect(width - 35 - cornerSize, height - 41, cornerSize, 6);
      ctx.fillRect(width - 41, height - 35 - cornerSize, 6, cornerSize);

      drawOverlayElements(ctx, width, height);
    };

    const drawOverlayElements = (ctx, w, h) => {
      const centerX = w / 2;

      // 1. Organization Header
      ctx.fillStyle = config.borderColor;
      ctx.font = "bold 20px Montserrat, sans-serif";
      ctx.textAlign = "center";
      ctx.letterSpacing = "4px";
      ctx.fillText(config.orgName.toUpperCase(), centerX, 120 + config.yOffsetOrg);

      // Subtitle
      ctx.fillStyle = config.textColor;
      ctx.font = "12px Montserrat, sans-serif";
      ctx.letterSpacing = "2px";
      ctx.fillText(config.subtitle.toUpperCase(), centerX, 145 + config.yOffsetOrg);

      // 2. Main Certificate Title
      ctx.fillStyle = config.borderColor;
      ctx.font = "bold 34px 'Playfair Display', Georgia, serif";
      ctx.letterSpacing = "1px";
      ctx.fillText(config.certTitle, centerX, 220 + config.yOffsetTitle);

      // 3. Statement Prefix ("This certifies that")
      ctx.fillStyle = config.textColor;
      ctx.font = "italic 16px 'Playfair Display', Georgia, serif";
      ctx.fillText(config.statementPrefix, centerX, 280 + config.yOffsetRecipient);

      // 4. Recipient Name (Highlight)
      ctx.fillStyle = config.recipientColor || config.accentColor;
      const recFont = config.recipientFontFamily === "Great Vibes"
        ? `normal ${config.fontSizeRecipient * 1.8}px 'Great Vibes', cursive`
        : `bold ${config.fontSizeRecipient * 1.5}px '${config.fontFamily}', Montserrat, sans-serif`;
      ctx.font = recFont;
      ctx.fillText(config.recipientName, centerX, 350 + config.yOffsetRecipient);

      // Underline bar under Recipient
      ctx.strokeStyle = `${config.borderColor}60`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(centerX - 240, 375 + config.yOffsetRecipient);
      ctx.lineTo(centerX + 240, 375 + config.yOffsetRecipient);
      ctx.stroke();

      // 5. Statement Suffix
      ctx.fillStyle = config.textColor;
      ctx.font = "14px Montserrat, sans-serif";
      ctx.letterSpacing = "0.5px";
      ctx.fillText(config.statementSuffix, centerX, 420 + config.yOffsetCourse);

      // 6. Course Title (Honoring Montserrat Size 22 Spec)
      ctx.fillStyle = config.borderColor;
      ctx.font = `normal ${config.fontSizeCourse}px 'Montserrat', sans-serif`;
      ctx.fillText(config.courseTitle, centerX, 470 + config.yOffsetCourse);

      // 7. Accreditation / Standards Notice
      ctx.fillStyle = `${config.textColor}99`;
      ctx.font = "12px Montserrat, sans-serif";
      ctx.fillText(config.accreditationNotice, centerX, 520 + config.yOffsetCourse);

      // 8. Signatures Section
      if (config.showSignatures) {
        const sigY = 740 + config.yOffsetSignatures;

        // Signer 1 (Left)
        const leftX = 280;
        ctx.strokeStyle = `${config.borderColor}80`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(leftX - 120, sigY);
        ctx.lineTo(leftX + 120, sigY);
        ctx.stroke();

        // Signature cursive rendering or custom image
        ctx.fillStyle = config.accentColor;
        ctx.font = "italic 28px 'Playfair Display', cursive";
        ctx.fillText(config.signer1SigText, leftX, sigY - 20);

        ctx.fillStyle = config.textColor;
        ctx.font = "bold 14px Montserrat, sans-serif";
        ctx.fillText(config.signer1Name, leftX, sigY + 25);
        ctx.fillStyle = `${config.textColor}90`;
        ctx.font = "11px Montserrat, sans-serif";
        ctx.fillText(config.signer1Title, leftX, sigY + 45);

        // Signer 2 (Right)
        const rightX = w - 280;
        ctx.beginPath();
        ctx.moveTo(rightX - 120, sigY);
        ctx.lineTo(rightX + 120, sigY);
        ctx.stroke();

        ctx.fillStyle = config.accentColor;
        ctx.font = "italic 28px 'Playfair Display', cursive";
        ctx.fillText(config.signer2SigText, rightX, sigY - 20);

        ctx.fillStyle = config.textColor;
        ctx.font = "bold 14px Montserrat, sans-serif";
        ctx.fillText(config.signer2Name, rightX, sigY + 25);
        ctx.fillStyle = `${config.textColor}90`;
        ctx.font = "11px Montserrat, sans-serif";
        ctx.fillText(config.signer2Title, rightX, sigY + 45);
      }

      // 9. Official Embossed Gold Seal (Center Bottom)
      if (config.showGoldSeal) {
        const sealX = centerX;
        const sealY = 740 + config.yOffsetSignatures;
        ctx.fillStyle = config.borderColor;
        ctx.beginPath();
        ctx.arc(sealX, sealY - 10, 48, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = config.bgColor;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(sealX, sealY - 10, 42, 0, Math.PI * 2);
        ctx.stroke();

        ctx.fillStyle = config.bgColor;
        ctx.font = "bold 10px Montserrat, sans-serif";
        ctx.fillText("OFFICIAL", sealX, sealY - 16);
        ctx.font = "bold 13px Montserrat, sans-serif";
        ctx.fillText("VERIFIED", sealX, sealY - 2);
        ctx.font = "8px Montserrat, sans-serif";
        ctx.fillText("SEAL 2026", sealX, sealY + 12);
      }

      // 10. Footer: Validity & Serial ID
      ctx.fillStyle = `${config.textColor}80`;
      ctx.font = "11px Montserrat, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(`SERIAL ID: ${config.certId}`, 70, 930);
      if (config.showExpiry) {
        ctx.fillText(config.validityText, 70, 950);
      }

      // 11. Scannable QR Code
      if (config.showQrCode) {
        const qrImg = new Image();
        qrImg.crossOrigin = "anonymous";
        qrImg.onload = () => {
          ctx.drawImage(qrImg, w - 160, 860, 90, 90);
        };
        qrImg.src = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(`https://deck.weddingsthyzfoto.com/verify/${config.certId}`)}`;
      }
    };

    drawContent();
  };

  // High-Resolution PNG Download
  const handleDownloadPNG = () => {
    setIsExporting(true);
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `Certificate_${config.recipientName.replace(/\s+/g, "_")}_${config.certId}.png`;
      a.click();
      setIsExporting(false);
      toast.success("High-Resolution Certificate PNG downloaded!");
    }, 300);
  };

  // Print / PDF Export
  const handlePrintPDF = () => {
    window.print();
  };

  // Save Config
  const handleSave = () => {
    localStorage.setItem("ld_certificate_template_config", JSON.stringify(config));
    if (onSaveTemplate) onSaveTemplate(config);
    toast.success("Certificate template & field layout saved successfully!");
    if (onClose) onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-7xl max-h-[94vh] flex flex-col shadow-2xl overflow-hidden font-sans">
        {/* Top Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/10 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 flex items-center justify-center">
              <Award className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                  Advanced Certificate Designer & Generator Studio
                </h2>
                <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 text-[10px]">
                  2-Year Dynamic Field Engine
                </Badge>
              </div>
              <p className="text-xs text-slate-500">
                Upload your own certificate design or choose a luxury base, map dynamic fields, adjust positions, and export print-ready credentials.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={handleDownloadPNG}
              disabled={isExporting}
              variant="outline"
              size="sm"
              className="text-xs gap-1.5 border-slate-300 dark:border-slate-700"
            >
              <Download className="w-3.5 h-3.5" />
              {isExporting ? "Rendering..." : "Download High-Res PNG"}
            </Button>
            <Button
              onClick={handleSave}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5 shadow-xs"
            >
              <Check className="w-3.5 h-3.5" />
              Save Template & Brand Kit
            </Button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 px-6 pt-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-xs shrink-0">
          {[
            { id: "design", label: "1. Design & Upload Background", icon: Palette },
            { id: "fields", label: "2. Dynamic Fields & Text", icon: Type },
            { id: "typography", label: "3. Fonts & Colors", icon: Sliders },
            { id: "positioning", label: "4. Field Alignment & Offsets", icon: MoveVertical },
            { id: "signatures", label: "5. Signatures, Seals & QR", icon: ShieldCheck }
          ].map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 py-2.5 px-3 border-b-2 font-medium transition-all ${
                  active
                    ? "border-blue-600 text-blue-600 dark:text-blue-400 font-bold"
                    : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Dual-Pane Studio Body */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
          {/* LEFT CONTROLS PANE (5 Cols) */}
          <div className="lg:col-span-5 p-6 border-r border-slate-200 dark:border-slate-800 overflow-y-auto space-y-5 bg-white dark:bg-slate-900 text-xs">
            
            {/* TAB 1: DESIGN & UPLOAD BACKGROUND */}
            {activeTab === "design" && (
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-blue-900 dark:text-blue-200 flex items-center gap-1.5">
                      <Upload className="w-4 h-4 text-blue-600" />
                      Upload Your Own Certificate Design
                    </span>
                    {config.customBgImage && (
                      <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Active Custom Design</Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-blue-800 dark:text-blue-300 mb-3">
                    Upload any custom graphic template, border layout, or company certificate PNG/JPG. The dynamic fields will render directly on top of your artwork!
                  </p>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleBgUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      Select Design File (PNG/JPG)
                    </Button>
                    {config.customBgImage && (
                      <Button
                        variant="outline"
                        onClick={() => setConfig(prev => ({ ...prev, customBgImage: null, templateId: "obsidian_gold" }))}
                        className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                      >
                        Reset to Default
                      </Button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-2">
                    Or Choose from Built-in Luxury Base Templates
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {PRESET_DESIGNS.map(preset => {
                      const isSelected = config.templateId === preset.id && !config.customBgImage;
                      return (
                        <div
                          key={preset.id}
                          onClick={() => handleSelectPreset(preset)}
                          className={`p-3.5 rounded-xl border cursor-pointer transition-all ${
                            isSelected
                              ? "border-blue-600 bg-blue-50/70 dark:bg-blue-950/40 shadow-xs ring-1 ring-blue-500"
                              : "border-slate-200 dark:border-slate-800 hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-slate-900 dark:text-slate-100">{preset.name}</span>
                            <span className="w-3.5 h-3.5 rounded-full border" style={{ backgroundColor: preset.bgColor, borderColor: preset.borderColor }} />
                          </div>
                          <p className="text-[11px] text-slate-500 leading-relaxed">{preset.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: DYNAMIC FIELDS & TEXT */}
            {activeTab === "fields" && (
              <div className="space-y-3.5">
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Student / Recipient Name *
                  </label>
                  <Input
                    value={config.recipientName}
                    onChange={(e) => setConfig({ ...config, recipientName: e.target.value })}
                    className="text-xs font-semibold text-blue-600"
                    placeholder="e.g. Jessica Taylor"
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">Dynamically populated when students complete course</p>
                </div>

                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Certificate Main Heading
                  </label>
                  <Input
                    value={config.certTitle}
                    onChange={(e) => setConfig({ ...config, certTitle: e.target.value })}
                    className="text-xs font-serif"
                  />
                </div>

                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Course / Specialization Title *
                  </label>
                  <Input
                    value={config.courseTitle}
                    onChange={(e) => setConfig({ ...config, courseTitle: e.target.value })}
                    className="text-xs"
                  />
                </div>

                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Issuing Organization / Academy
                  </label>
                  <Input
                    value={config.orgName}
                    onChange={(e) => setConfig({ ...config, orgName: e.target.value })}
                    className="text-xs"
                  />
                </div>

                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Curriculum Statement / Subtitle
                  </label>
                  <Textarea
                    value={config.statementSuffix}
                    onChange={(e) => setConfig({ ...config, statementSuffix: e.target.value })}
                    className="text-xs min-h-[60px]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">Certificate Serial ID</label>
                    <Input
                      value={config.certId}
                      onChange={(e) => setConfig({ ...config, certId: e.target.value })}
                      className="text-xs font-mono"
                    />
                  </div>
                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">Issue Date</label>
                    <Input
                      value={config.issueDate}
                      onChange={(e) => setConfig({ ...config, issueDate: e.target.value })}
                      className="text-xs"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">Validity & Expiration Protocol</label>
                  <Input
                    value={config.validityText}
                    onChange={(e) => setConfig({ ...config, validityText: e.target.value })}
                    className="text-xs bg-slate-50 dark:bg-slate-800"
                  />
                </div>
              </div>
            )}

            {/* TAB 3: TYPOGRAPHY & COLORS */}
            {activeTab === "typography" && (
              <div className="space-y-4">
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Primary Certificate Font Family
                  </label>
                  <select
                    value={config.fontFamily}
                    onChange={(e) => setConfig({ ...config, fontFamily: e.target.value })}
                    className="w-full h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 text-xs"
                  >
                    <option value="Montserrat">Montserrat (Standard CRM Spec)</option>
                    <option value="Playfair Display">Playfair Display (Classical University)</option>
                    <option value="Cinzel">Cinzel (Roman Heraldic Engraving)</option>
                    <option value="Inter">Inter (Clean Modern Tech)</option>
                    <option value="Great Vibes">Great Vibes (Formal Calligraphy)</option>
                  </select>
                </div>

                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Recipient Name Font Style
                  </label>
                  <select
                    value={config.recipientFontFamily}
                    onChange={(e) => setConfig({ ...config, recipientFontFamily: e.target.value })}
                    className="w-full h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 text-xs"
                  >
                    <option value="Montserrat">Montserrat (Bold Modern)</option>
                    <option value="Playfair Display">Playfair Display (Serif Elegance)</option>
                    <option value="Great Vibes">Great Vibes (Formal Calligraphy Script)</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">Recipient Text Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config.recipientColor}
                        onChange={(e) => setConfig({ ...config, recipientColor: e.target.value })}
                        className="w-8 h-8 rounded border p-0.5 cursor-pointer"
                      />
                      <Input
                        value={config.recipientColor}
                        onChange={(e) => setConfig({ ...config, recipientColor: e.target.value })}
                        className="text-xs font-mono h-8"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">Gold Foil / Trim Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config.borderColor}
                        onChange={(e) => setConfig({ ...config, borderColor: e.target.value })}
                        className="w-8 h-8 rounded border p-0.5 cursor-pointer"
                      />
                      <Input
                        value={config.borderColor}
                        onChange={(e) => setConfig({ ...config, borderColor: e.target.value })}
                        className="text-xs font-mono h-8"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">Body Text Color</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config.textColor}
                        onChange={(e) => setConfig({ ...config, textColor: e.target.value })}
                        className="w-8 h-8 rounded border p-0.5 cursor-pointer"
                      />
                      <Input
                        value={config.textColor}
                        onChange={(e) => setConfig({ ...config, textColor: e.target.value })}
                        className="text-xs font-mono h-8"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">Background Fill</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={config.bgColor}
                        onChange={(e) => setConfig({ ...config, bgColor: e.target.value })}
                        className="w-8 h-8 rounded border p-0.5 cursor-pointer"
                      />
                      <Input
                        value={config.bgColor}
                        onChange={(e) => setConfig({ ...config, bgColor: e.target.value })}
                        className="text-xs font-mono h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 4: FIELD ALIGNMENT & VERTICAL OFFSETS */}
            {activeTab === "positioning" && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-[11px] text-slate-600 dark:text-slate-300">
                  Use these vertical alignment sliders to shift fields up or down so they align cleanly over your custom design template.
                </div>

                <div>
                  <div className="flex items-center justify-between font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    <span>Recipient Name Vertical Offset</span>
                    <span className="font-mono text-blue-600">{config.yOffsetRecipient}px</span>
                  </div>
                  <input
                    type="range"
                    min="-80"
                    max="80"
                    value={config.yOffsetRecipient}
                    onChange={(e) => setConfig({ ...config, yOffsetRecipient: Number(e.target.value) })}
                    className="w-full accent-blue-600 cursor-pointer"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    <span>Course Title Vertical Offset</span>
                    <span className="font-mono text-blue-600">{config.yOffsetCourse}px</span>
                  </div>
                  <input
                    type="range"
                    min="-80"
                    max="80"
                    value={config.yOffsetCourse}
                    onChange={(e) => setConfig({ ...config, yOffsetCourse: Number(e.target.value) })}
                    className="w-full accent-blue-600 cursor-pointer"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    <span>Signatures & Seal Vertical Offset</span>
                    <span className="font-mono text-blue-600">{config.yOffsetSignatures}px</span>
                  </div>
                  <input
                    type="range"
                    min="-80"
                    max="80"
                    value={config.yOffsetSignatures}
                    onChange={(e) => setConfig({ ...config, yOffsetSignatures: Number(e.target.value) })}
                    className="w-full accent-blue-600 cursor-pointer"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    <span>Recipient Font Scale</span>
                    <span className="font-mono text-blue-600">{config.fontSizeRecipient}px</span>
                  </div>
                  <input
                    type="range"
                    min="20"
                    max="44"
                    value={config.fontSizeRecipient}
                    onChange={(e) => setConfig({ ...config, fontSizeRecipient: Number(e.target.value) })}
                    className="w-full accent-blue-600 cursor-pointer"
                  />
                </div>
              </div>
            )}

            {/* TAB 5: SIGNATURES, SEALS & QR */}
            {activeTab === "signatures" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">Signer 1 (Primary)</label>
                    <Input
                      value={config.signer1Name}
                      onChange={(e) => setConfig({ ...config, signer1Name: e.target.value })}
                      className="text-xs"
                    />
                    <Input
                      value={config.signer1Title}
                      onChange={(e) => setConfig({ ...config, signer1Title: e.target.value })}
                      className="text-xs mt-1"
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">Signer 2 (Dean / Director)</label>
                    <Input
                      value={config.signer2Name}
                      onChange={(e) => setConfig({ ...config, signer2Name: e.target.value })}
                      className="text-xs"
                    />
                    <Input
                      value={config.signer2Title}
                      onChange={(e) => setConfig({ ...config, signer2Title: e.target.value })}
                      className="text-xs mt-1"
                    />
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-200 dark:border-slate-800 space-y-2.5">
                  <span className="font-semibold text-slate-700 dark:text-slate-300 block">Element Display Toggles</span>
                  
                  <label className="flex items-center justify-between cursor-pointer p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <span>Show Scannable QR Verification Code</span>
                    <input
                      type="checkbox"
                      checked={config.showQrCode}
                      onChange={(e) => setConfig({ ...config, showQrCode: e.target.checked })}
                      className="h-4 w-4 rounded text-blue-600"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <span>Show 24K Embossed Gold Seal</span>
                    <input
                      type="checkbox"
                      checked={config.showGoldSeal}
                      onChange={(e) => setConfig({ ...config, showGoldSeal: e.target.checked })}
                      className="h-4 w-4 rounded text-blue-600"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <span>Show Signatures Row</span>
                    <input
                      type="checkbox"
                      checked={config.showSignatures}
                      onChange={(e) => setConfig({ ...config, showSignatures: e.target.checked })}
                      className="h-4 w-4 rounded text-blue-600"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT LIVE HIGH-RES CANVAS PREVIEW (7 Cols) */}
          <div className="lg:col-span-7 p-6 bg-slate-100 dark:bg-slate-950 flex flex-col justify-center items-center overflow-hidden">
            <div className="w-full flex items-center justify-between mb-3 text-xs">
              <span className="font-semibold text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                <Eye className="w-4 h-4 text-blue-600" />
                Live 16:10 Print Canvas (Real-Time Render)
              </span>
              <span className="font-mono text-[11px] text-amber-500 font-bold">
                1600 × 1000 High-Res Output
              </span>
            </div>

            {/* The Live Interactive Canvas */}
            <div className="w-full max-w-2xl aspect-[16/10] rounded-xl shadow-2xl border border-slate-300 dark:border-slate-800 overflow-hidden bg-black flex items-center justify-center relative">
              <canvas
                ref={canvasRef}
                className="w-full h-full object-contain"
              />
            </div>

            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={handleDownloadPNG}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5 shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                Export High-Res PNG
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="sm"
                className="text-xs gap-1.5 border-slate-300 dark:border-slate-700"
              >
                <Printer className="w-3.5 h-3.5" />
                Print / Save PDF
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

